import { useState, useEffect, useCallback } from "react";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";

const testimonials = [
  {
    quote: "It was such a great experience with Tammy.",
    name: "Carrie M.",
  },
  {
    quote:
      "I healed, released and then rose up like a Phoenix. Reborn. Thank you, Align Wellness.",
    name: "Alisha K.",
  },
  {
    quote:
      "What a wonderful experience — it felt great to let go of some unneeded baggage. Thank you, Tammy.",
    name: "Kristen L.",
  },
];

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const count = testimonials.length;

  const goTo = useCallback((i) => setIndex(((i % count) + count) % count), [count]);
  const next = useCallback(() => goTo(index + 1), [index, goTo]);
  const prev = useCallback(() => goTo(index - 1), [index, goTo]);

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % count), 6000);
    return () => clearInterval(id);
  }, [count]);

  const active = testimonials[index];

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-24 lg:py-32 bg-cream-soft overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute top-10 left-1/2 -translate-x-1/2 h-80 w-80 rounded-full bg-[#D8B4A0]/15 blur-3xl"
      />

      <div className="relative mx-auto max-w-4xl px-6 lg:px-10 text-center">
        <p className="text-xs tracking-eyebrow uppercase text-sage mb-6">
          Success Stories
        </p>
        <h2
          data-testid="testimonials-headline"
          className="font-serif-display text-forest text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
        >
          Words from those who
          <br />
          <span className="italic">rose up.</span>
        </h2>

        <div className="relative mt-14 min-h-[220px] sm:min-h-[200px] flex items-center justify-center">
          <Quote
            size={48}
            className="absolute -top-4 left-1/2 -translate-x-1/2 text-[#D8B4A0]/60"
            strokeWidth={1}
          />
          <div key={index} className="aw-fade px-4 sm:px-16">
            <blockquote
              data-testid="testimonial-quote"
              className="font-serif-display italic text-forest text-2xl sm:text-3xl lg:text-4xl leading-snug"
            >
              “{active.quote}”
            </blockquote>
            <p
              data-testid="testimonial-name"
              className="mt-8 text-[11px] tracking-eyebrow uppercase text-sage"
            >
              {active.name}
            </p>
          </div>
        </div>

        <div className="mt-10 flex items-center justify-center gap-6">
          <button
            type="button"
            onClick={prev}
            data-testid="testimonial-prev"
            aria-label="Previous testimonial"
            className="h-11 w-11 rounded-full border border-forest/20 flex items-center justify-center text-forest transition-all duration-500 hover:bg-forest hover:text-cream hover:scale-95"
          >
            <ChevronLeft size={18} />
          </button>

          <div className="flex items-center gap-2.5">
            {testimonials.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                data-testid={`testimonial-dot-${i}`}
                aria-label={`Go to testimonial ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-500 ${
                  i === index ? "w-8 bg-forest" : "w-2 bg-forest/25 hover:bg-forest/40"
                }`}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={next}
            data-testid="testimonial-next"
            aria-label="Next testimonial"
            className="h-11 w-11 rounded-full border border-forest/20 flex items-center justify-center text-forest transition-all duration-500 hover:bg-forest hover:text-cream hover:scale-95"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}
